import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Calendar, TrendingUp, TrendingDown, Award, AlertCircle } from 'lucide-react';
import { resolveMediaUrl } from '../../utils/helpers';

// Mock attendance data
const mockAttendanceData = {
  overallStats: {
    averageAttendanceRate: 91.7,
    totalSessions: 27,
    highestAttendance: 20,
    lowestAttendance: 12,
    trend: 'up' // up, down, stable
  },
  studentAttendance: [
    {
      userId: 201,
      firstName: 'Alex',
      lastName: 'Martinez',
      profileImage: 'https://i.pravatar.cc/150?img=11',
      attended: 25,
      total: 27,
      rate: 92.5,
      trend: 'stable',
      consecutiveAbsences: 0
    },
    {
      userId: 202,
      firstName: 'Emma',
      lastName: 'Chen',
      profileImage: 'https://i.pravatar.cc/150?img=12',
      attended: 27,
      total: 27,
      rate: 100,
      trend: 'up',
      consecutiveAbsences: 0
    },
    {
      userId: 203,
      firstName: 'James',
      lastName: 'Wilson',
      profileImage: 'https://i.pravatar.cc/150?img=13',
      attended: 22,
      total: 25,
      rate: 88.0,
      trend: 'down',
      consecutiveAbsences: 2
    }
  ],
  recentSessions: [
    {
      date: '2026-02-28',
      attended: 13,
      total: 15,
      rate: 86.7
    },
    {
      date: '2026-02-26',
      attended: 15,
      total: 15,
      rate: 100
    },
    {
      date: '2026-02-24',
      attended: 14,
      total: 15,
      rate: 93.3
    }
  ]
};

const ClassAttendanceTab = ({ classId, classData }) => {
  const { theme } = useTheme();
  const [sortBy, setSortBy] = useState('rate'); // rate, name, attended

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp size={16} className="text-green-600" />;
      case 'down':
        return <TrendingDown size={16} className="text-red-600" />;
      default:
        return <div className="w-4 h-0.5 bg-gray-400" />;
    }
  };

  const getRateColor = (rate) => {
    if (rate >= 95) return 'text-green-600';
    if (rate >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const sortedStudents = [...mockAttendanceData.studentAttendance].sort((a, b) => {
    switch (sortBy) {
      case 'rate':
        return b.rate - a.rate;
      case 'name':
        return a.lastName.localeCompare(b.lastName);
      case 'attended':
        return b.attended - a.attended;
      default:
        return 0;
    }
  });

  return (
    <div>
      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className={`${theme.card} p-6 rounded-xl border ${theme.border}`}>
          <div className={`text-sm ${theme.textSecondary} mb-2`}>Average Attendance</div>
          <div className="flex items-end justify-between">
            <div className={`text-3xl font-bold ${getRateColor(mockAttendanceData.overallStats.averageAttendanceRate)}`}>
              {mockAttendanceData.overallStats.averageAttendanceRate}%
            </div>
            {getTrendIcon(mockAttendanceData.overallStats.trend)}
          </div>
        </div>

        <div className={`${theme.card} p-6 rounded-xl border ${theme.border}`}>
          <div className={`text-sm ${theme.textSecondary} mb-2`}>Total Sessions</div>
          <div className={`text-3xl font-bold ${theme.text}`}>
            {mockAttendanceData.overallStats.totalSessions}
          </div>
        </div>

        <div className={`${theme.card} p-6 rounded-xl border ${theme.border}`}>
          <div className={`text-sm ${theme.textSecondary} mb-2`}>Highest Attendance</div>
          <div className={`text-3xl font-bold text-green-600`}>
            {mockAttendanceData.overallStats.highestAttendance}
          </div>
        </div>

        <div className={`${theme.card} p-6 rounded-xl border ${theme.border}`}>
          <div className={`text-sm ${theme.textSecondary} mb-2`}>Lowest Attendance</div>
          <div className={`text-3xl font-bold text-red-600`}>
            {mockAttendanceData.overallStats.lowestAttendance}
          </div>
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="mb-8">
        <h3 className={`text-lg font-bold ${theme.text} mb-4`}>Recent Sessions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {mockAttendanceData.recentSessions.map((session, index) => (
            <div key={index} className={`${theme.card} p-4 rounded-xl border ${theme.border}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`text-sm ${theme.textSecondary}`}>{formatDate(session.date)}</div>
                <div className={`text-lg font-bold ${getRateColor(session.rate)}`}>
                  {session.rate}%
                </div>
              </div>
              <div className="text-sm">
                <span className="font-semibold">{session.attended}</span> of{' '}
                <span className="font-semibold">{session.total}</span> attended
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${session.rate}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Student Attendance List */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-lg font-bold ${theme.text}`}>Student Attendance</h3>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={`px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 text-sm`}
          >
            <option value="rate">Sort by Rate</option>
            <option value="name">Sort by Name</option>
            <option value="attended">Sort by Sessions Attended</option>
          </select>
        </div>

        <div className="space-y-3">
          {sortedStudents.map(student => (
            <div
              key={student.userId}
              className={`${theme.card} p-4 rounded-xl border ${theme.border} ${
                student.consecutiveAbsences >= 2 ? 'ring-2 ring-orange-400' : ''
              }`}
            >
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <img
                  src={resolveMediaUrl(student.profileImage)}
                  alt={`${student.firstName} ${student.lastName}`}
                  className="w-12 h-12 rounded-full object-cover"
                />

                {/* Name & Stats */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`font-semibold ${theme.text}`}>
                      {student.firstName} {student.lastName}
                    </h4>
                    {student.rate === 100 && (
                      <Award size={16} className="text-yellow-500" title="Perfect Attendance" />
                    )}
                    {student.consecutiveAbsences >= 2 && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                        <AlertCircle size={12} />
                        <span>{student.consecutiveAbsences} consecutive absences</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className={theme.textSecondary}>
                      {student.attended}/{student.total} sessions
                    </span>
                    <div className="flex items-center gap-1">
                      {getTrendIcon(student.trend)}
                      <span className={`font-semibold ${getRateColor(student.rate)}`}>
                        {student.rate}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="hidden md:block w-48">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        student.rate >= 95 ? 'bg-green-600' :
                        student.rate >= 80 ? 'bg-yellow-600' : 'bg-red-600'
                      }`}
                      style={{ width: `${student.rate}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className={`mt-6 p-4 ${theme.card} rounded-xl border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-600" />
            <span className={theme.textSecondary}>95%+ Attendance</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-600" />
            <span className={theme.textSecondary}>80-94% Attendance</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-600" />
            <span className={theme.textSecondary}>Below 80%</span>
          </div>
          <div className="flex items-center gap-2">
            <Award size={16} className="text-yellow-500" />
            <span className={theme.textSecondary}>Perfect Attendance</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassAttendanceTab;
